import React from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { FaRegCalendarAlt } from "react-icons/fa";
import { postApplyLeave } from "../../ApiHelper/url_Helpers";
import { Modal } from "reactstrap";
import { toasterError, toasterSuccess } from "../../Components/toaster";
import { FiArrowLeft, FiX } from "react-icons/fi";

const validationSchema = Yup.object({
  leave_type: Yup.string().required("Leave type is required"),
  start_date: Yup.date().required("Start date is required"),
  end_date: Yup.date()
    .required("End date is required")
    .min(Yup.ref("start_date"), "End date cannot be before start date"),
  reason: Yup.string()
    .required("Reason is required")
    .min(5, "Minimum 5 characters required"),
});

const ApplyLeaveModal = ({ open, onClose, setIsApply }) => {
  const leaveType_List = JSON?.parse(localStorage?.getItem("leaveType"));
  if (!open) return null;
  const initialValues = {
    leave_type: "",
    start_date: "",
    end_date: "",
    reason: "",
  };
  const today = new Date();
  const currentYear = today.getFullYear();
  const minDate = `${currentYear}-01-01`;
  
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const leaveType = leaveType_List?.find(
      (type) => type?.name === values?.leave_type
    );
    const ruleId = leaveType?.policy_rule_id;
    const payload = {
      policy_rule_id: ruleId,
      leave_type: values.leave_type,
      start_date: values.start_date,
      end_date: values.end_date,
      reason: values.reason,
    };


    try {
      // 🔹 API CALL
      const response = await postApplyLeave(payload);
      setIsApply(response);
      toasterSuccess("Leave applied successfully");
      response && resetForm();
      response && onClose();
    } catch (error) {
      console.log("Failed to apply leave:", error);
    } finally {
      onClose();
      setSubmitting(false);
    }
  };

  return (
    // <Modal open={open} onClose={onClose}>
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
      <div className="bg-white w-full max-w-lg rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="flex  border-b border-gray-200 pb-0.5 justify-between items-center">
          <div className="flex items-center gap-2 md:gap-3 p-4 md:p-4">
            <button
              onClick={onClose}
              className="text-md md:text-2xl text-gray-700 hover:text-black transition cursor-pointer"
            >
              <FiArrowLeft />
            </button>
            <h2 className="text-md md:text-lg font-semibold">
              Apply Leave
            </h2>
          </div>

          <div
            onClick={onClose}
            className="w-4.5 h-4.5 px-0.5 flex items-center justify-center
                                  border-2 border-[#717171] rounded-md mr-6
                                  cursor-pointer transition-all duration-200
                                  hover:border-gray-600 hover:shadow-md"
          >
            <FiX className="w-4 h-4 font-semibold text-[#717171]" />
          </div>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form>
              {/* Body */}
              <div className="p-6 space-y-4">
                {/* Leave Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Leave Type
                  </label>
                  <Field
                    as="select"
                    name="leave_type"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-cyan-500 outline-none"
                  >
                    <option value="" >Select Leave Type</option>
                    {(leaveType_List)?.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name="leave_type"
                    component="p"
                    className="text-xs text-red-500 mt-1"
                  />
                </div>

                {/* From - To */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      From
                    </label>
                    <div className="relative">
                      <Field
                        type="date"
                        name="start_date"
                        min={minDate}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-cyan-500 outline-none"
                      />
                      <FaRegCalendarAlt className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                    <ErrorMessage
                      name="start_date"
                      component="p"
                      className="text-xs text-red-500 mt-1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      To
                    </label>
                    <div className="relative">
                      <Field
                        type="date"
                        name="end_date"
                        disabled={!values.start_date}
                        min={values.start_date || new Date()?.toISOString()?.split("T")[0]}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 focus:ring-2 focus:ring-cyan-500 outline-none"
                      />
                      <FaRegCalendarAlt className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    </div>
                    <ErrorMessage
                      name="end_date"
                      component="p"
                      className="text-xs text-red-500 mt-1"
                    />
                  </div>
                </div>

                {/* Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason For Leave
                  </label>
                  <Field
                    as="textarea"
                    rows="4"
                    name="reason"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 resize-none focus:ring-2 focus:ring-cyan-500 outline-none"
                    placeholder="Enter reason..."
                  />
                  <ErrorMessage
                    name="reason"
                    component="p"
                    className="text-xs text-red-500 mt-1"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end items-center gap-4 px-6 py-4 border-t">
                <button
                  type="button"
                  onClick={onClose}
                  className="text-gray-600 hover:text-gray-900 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-cyan-500 hover:bg-cyan-600 disabled:opacity-50 text-white px-6 py-2 rounded-lg font-semibold transition"
                >
                  {isSubmitting ? "Applying..." : "Apply"}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
    // </Modal>
  );
};

export default ApplyLeaveModal;
